import { useEffect, useRef, useState } from "react";
import { Grid, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide, SwiperClass } from "swiper/react";
import { postOfferView } from "../../../../services/offers/offers.service";
import "swiper/css";
import "swiper/css/grid";
import {
  CampaignType,
  Offer,
  OffersIdSlots,
  PlacementStage,
  QueryPostOfferView,
  Template,
  TemplateOptionSettings,
} from "../../../../types/global.types";
import OfferItem from "../offer-item/OfferItem.component";
import { getSettledSlideDirection } from "../../../../utility/swiperSlideTransition";

interface Props {
  settings: TemplateOptionSettings;
  settingsTemplate: Template;
  campaign: CampaignType;
  displayOnly: boolean;
  campaignId: string;
  placementId: string;
  placementName: string;
  offers: Offer[];
  isInline?: boolean;
  isSeen: boolean;
  sessionId: string;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  layoutId: string;
  canAutoSendEmailFromLabel: boolean;
  canAutoSendEmailFromMainCTA: boolean;
  onAutoSendEmailFromLabel: (offer: Offer, slot: number) => Promise<void>;
  autoSendingOfferId: string;
}

const SwiperComponent = ({
  settings,
  campaign,
  offers,
  displayOnly,
  campaignId,
  placementId,
  placementName,
  settingsTemplate,
  openSingle,
  onAdd,
  isSeen,
  sessionId,
  layoutId,
  canAutoSendEmailFromLabel,
  canAutoSendEmailFromMainCTA,
  onAutoSendEmailFromLabel,
  autoSendingOfferId,
}: Props) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [eventOfferIds, setEventOfferIds] = useState<OffersIdSlots[]>([]);
  const [eventSentOnIndex, setEventSentOnIndex] = useState<number[]>([]);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [swiperWidth, setSwiperWidth] = useState(0);
  const [paginationNeeded, setPaginationNeeded] = useState(false);
  const [filteredOffers, setFilteredOffers] = useState<Offer[]>(offers);
  // swiper item that are fully displayed with margin 20px
  const swiperItemDisplayed = Math.ceil(swiperWidth / 250);

  // swiper item that are fully displayed with last item without margin
  const swiperSize =
    Math.ceil((swiperWidth - swiperItemDisplayed * 20) / 230) * 2;

  const refNavPrev = useRef<HTMLDivElement | null>(null);
  const refNavNext = useRef<HTMLDivElement | null>(null);
  const [previousIndex, setPreviousIndex] = useState<number>(0);
  const lastTrackedRealIndexRef = useRef(0);

  const setDefaultOfferImpressionEvent = async (offers: OffersIdSlots[]) => {
    if (eventOfferIds.length < campaign.offers.length) {
      const data: QueryPostOfferView = {
        offers,
        campaignId,
        campaignName: campaign.name || "",
        companyId: campaign.company_Id,
        placementId,
        placementName,
        pageUrl: "",
        templateId: settingsTemplate.identifier,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        layoutId: layoutId,
      };

      await postOfferView(data);
    }
  };

  useEffect(() => {
    setFilteredOffers(offers);
  }, [offers]);

  useEffect(() => {
    // Check if the element is defined
    if (elementRef.current) {
      // Get the width of the element
      setSwiperWidth(elementRef?.current?.offsetWidth);
    }
  }, []);

  //set event for default active slide offers
  useEffect(() => {
    if (swiperSize !== 0) {
      setPaginationNeeded(offers.length > swiperSize);
    }
    // eslint-disable-next-line
  }, [swiperSize]);

  //set event for default active slide offers
  useEffect(() => {
    if (swiper && swiperSize !== 0 && isSeen) {
      const _ids = campaign.offers
        .map((offer: Offer, index: number) => ({
          id: offer.id,
          slot: index + 1,
        }))
        .slice(
          0,
          swiperSize !== 0
            ? settingsTemplate.deviceType === "mobile"
              ? 2
              : swiperSize
            : 2
        );
      setDefaultOfferImpressionEvent(_ids);
      setEventOfferIds(_ids);
      setPaginationNeeded(offers.length > swiperSize);
    }
    // eslint-disable-next-line
  }, [swiper, swiperSize, isSeen]);

  useEffect(() => {
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!swiper) return;
      if (e.key === "ArrowLeft") {
        swiper.slidePrev();
      } else if (e.key === "ArrowRight") {
        swiper.slideNext();
      }
    };
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [swiper]);

  const setOfferViewEvent = (activeIndex: number, next: boolean) => {
    const mainIndex = next ? previousIndex + 1 : previousIndex - 1;

    if (activeIndex !== 0 && activeIndex > previousIndex) {
      const sliceFrom = swiperSize * mainIndex;
      const sliceTo = swiperSize * (mainIndex + 1);
      const slot = swiperSize * mainIndex;

      const viewedOffersId = campaign.offers
        .slice(sliceFrom, sliceTo)
        .map((offer: Offer, index: number) => ({
          id: offer.id,
          slot: slot + index + 1,
        }));

      if (!eventSentOnIndex.includes(activeIndex)) {
        setDefaultOfferImpressionEvent(viewedOffersId);
        setEventOfferIds(viewedOffersId);
        setEventSentOnIndex((prevState) => [...prevState, activeIndex]);
      }
    }

  };

  const setOfferViewEventMobile = (activeIndex: number, next: boolean) => {
    const mainIndex = next ? previousIndex + 1 : previousIndex - 1;

    if (activeIndex !== 0 && activeIndex > previousIndex) {
      const sliceFrom = 2 * mainIndex;
      const sliceTo = 2 * (mainIndex + 1);
      const slot = 2 * mainIndex;

      const viewedOffersId = campaign.offers
        .slice(sliceFrom, sliceTo)
        .map((offer: Offer, index: number) => ({
          id: offer.id,
          slot: slot + index + 1,
        }));

      if (!eventSentOnIndex.includes(activeIndex)) {
        setDefaultOfferImpressionEvent(viewedOffersId);
        setEventOfferIds(viewedOffersId);
        setEventSentOnIndex((prevState) => [...prevState, activeIndex]);
      }
    }

  };

  return (
    <div ref={elementRef}>
      {offers.length > 0 && isSeen && (
        <Swiper
          className={
            !paginationNeeded
              ? `swiper-container dme6-swiper-container ${settingsTemplate.deviceType === "mobile"
                ? ""
                : `dme6-swiper-container-${settings?.mainLayout?.align} ${offers.length < swiperSize / 2
                  ? "swiper-container-nopagination"
                  : ""
                }`
              } swiper-inline `
              : `swiper-container dme6-swiper-container swiper-inline`
          }
          modules={[Grid, Navigation]}
          grid={{
            rows: offers.length > 4 ? 2 : 1,
            fill: "column",
          }}
          slidesPerView={settingsTemplate.deviceType === "mobile" ? 1 : "auto"}
          slidesPerGroup={
            settingsTemplate.deviceType === "mobile" ? 1 : swiperSize / 2
          }
          onSwiper={(s: SwiperClass) => {
            setSwiper(s);
            lastTrackedRealIndexRef.current = s.realIndex;
          }}
          watchSlidesProgress={true}
          navigation={{
            prevEl: refNavPrev.current,
            nextEl: refNavNext.current,
          }}
          onBeforeInit={(swiper: any) => {
            swiper.params.navigation.prevEl = refNavPrev.current;
            swiper.params.navigation.nextEl = refNavNext.current;
          }}
          onTransitionEnd={(_swiper: SwiperClass) => {
            const activeIndex = _swiper.realIndex;
            const direction = getSettledSlideDirection(
              _swiper,
              lastTrackedRealIndexRef.current
            );
            if (direction === null) return;
            lastTrackedRealIndexRef.current = activeIndex;
            const isNext = direction === "next";
            if (settingsTemplate.deviceType === "mobile") {
              setOfferViewEventMobile(activeIndex, isNext);
            } else {
              setOfferViewEvent(activeIndex, isNext);
            }
            setPreviousIndex((prev) => (isNext ? prev + 1 : prev - 1));
          }}
          preventInteractionOnTransition={
            settingsTemplate.deviceType === "mobile" ? true : false
          }
        >
          {filteredOffers.length > 0 &&
            filteredOffers.map((o: Offer, i: number) => {
              return (
                <SwiperSlide
                  key={i}
                  style={{ color: settings?.dealLayout.textColor }}
                >
                  <OfferItem
                    sessionId={sessionId}
                    offer={o}
                    slot={i + 1}
                    settings={settings.dealLayout}
                    mainSettings={settings.mainLayout}
                    isAdded={o.isAdded || false}
                    settingsTemplate={settingsTemplate}
                    onAdd={onAdd}
                    displayOnly={displayOnly}
                    campaign={campaign}
                    retailerId={campaign?.company_Id}
                    campaignId={campaignId}
                    placementId={placementId}
                    placementName={placementName}
                    openSingle={openSingle}
                    hasVoucher={campaign.offers.some(
                      (offer) => offer.voucher && offer.voucher.voucherType
                    )}
                    otherSettings={settings.otherSettings}
                    layoutId={layoutId}
                    layoutOptions={settings.layoutOptions}
                    canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
                    canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
                    onAutoSendEmailFromLabel={onAutoSendEmailFromLabel}
                    autoSendingOfferId={autoSendingOfferId === o.id}
                  />
                </SwiperSlide>
              );
            })}
          <div className="swiper-navigation-dme6">
            <div
              className="swiper-button-prev"
              ref={refNavPrev}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "ArrowLeft") {
                  swiper?.slidePrev();
                }

                if (e.key === "Enter") {
                  swiper?.slidePrev();
                }
              }}
            >
              <svg
                width="34"
                height="34"
                viewBox="0 0 34 34"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="18.6094"
                  y="23"
                  width="8.6192"
                  height="1.72384"
                  rx="0.86192"
                  transform="rotate(-135 18.6094 23)"
                  fill={settings.mainLayout.textColor}
                />
                <rect
                  x="20.3135"
                  y="12.2188"
                  width="8.6192"
                  height="1.72384"
                  rx="0.86192"
                  transform="rotate(135 20.3135 12.2188)"
                  fill={settings.mainLayout.textColor}
                />
              </svg>
            </div>
            <div
              className="swiper-button-next"
              ref={refNavNext}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight") {
                  swiper?.slideNext();
                }

                if (e.key === "Enter") {
                  swiper?.slideNext();
                }
              }}
            >
              <svg
                width="34"
                height="34"
                viewBox="0 0 34 34"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="15.3906"
                  y="11"
                  width="8.6192"
                  height="1.72384"
                  rx="0.86192"
                  transform="rotate(45 15.3906 11)"
                  fill={settings.mainLayout.textColor}
                />
                <rect
                  x="14.1719"
                  y="22.2656"
                  width="8.6192"
                  height="1.72384"
                  rx="0.86192"
                  transform="rotate(-45 14.1719 22.2656)"
                  fill={settings.mainLayout.textColor}
                />
              </svg>
            </div>
          </div>
        </Swiper>
      )}
    </div>
  );
};

export default SwiperComponent;
